import { useHistory, useLocation } from 'react-router';
import { LocationDescriptorObject } from 'history';

/**
 * Custom hook that extends the standard React Router history with query string preservation
 * 
 * @returns Extended history object with pushWithQuery and replaceWithQuery methods
 */
export const useHistoryWithQuery = () => {
  const history = useHistory();
  const location = useLocation();
  
  /**
   * Navigate to a new path while preserving the current query string
   * @param pathOrDescriptor - The path string or location descriptor object to navigate to
   */
  const pushWithQuery = (pathOrDescriptor: string | LocationDescriptorObject) => {
    if (typeof pathOrDescriptor === 'string') {
      history.push(pathOrDescriptor + location.search);
    } else {
      // If it's an object, merge the current search with any existing search
      const mergedDescriptor = {
        ...pathOrDescriptor,
        search: pathOrDescriptor.search ? pathOrDescriptor.search + '&' + location.search.substring(1) : location.search
      };
      history.push(mergedDescriptor);
    }
  };
  
  /**
   * Replace the current path while preserving the current query string
   * @param pathOrDescriptor - The path string or location descriptor object to replace with
   */
  const replaceWithQuery = (pathOrDescriptor: string | LocationDescriptorObject) => {
    if (typeof pathOrDescriptor === 'string') {
      history.replace(pathOrDescriptor + location.search);
    } else {
      // If it's an object, merge the current search with any existing search
      const mergedDescriptor = {
        ...pathOrDescriptor,
        search: pathOrDescriptor.search ? pathOrDescriptor.search + '&' + location.search.substring(1) : location.search
      };
      history.replace(mergedDescriptor);
    }
  };
  
  /**
   * Navigate to a new path with optional query string preservation
   * @param pathOrDescriptor - The path string or location descriptor object to navigate to
   * @param preserveQuery - Whether to preserve the current query string (default: true)
   */
  const pushConditional = (pathOrDescriptor: string | LocationDescriptorObject, preserveQuery: boolean = true) => {
    if (preserveQuery) {
      pushWithQuery(pathOrDescriptor);
    } else {
      history.push(pathOrDescriptor);
    }
  };
  
  /**
   * Replace the current path with optional query string preservation
   * @param pathOrDescriptor - The path string or location descriptor object to replace with
   * @param preserveQuery - Whether to preserve the current query string (default: true)
   */
  const replaceConditional = (pathOrDescriptor: string | LocationDescriptorObject, preserveQuery: boolean = true) => {
    if (preserveQuery) {
      replaceWithQuery(pathOrDescriptor);
    } else {
      history.replace(pathOrDescriptor);
    }
  };
  
  return {
    ...history,
    pushWithQuery,
    replaceWithQuery,
    pushConditional,
    replaceConditional
  };
};